import { BannerItem } from "@/types/pages/blocks"
import BannerCarouselIndicator from "@/components/Banner/BannerCarouselIndicator"
import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import BannerContent from "./BannerContent"

interface BannerCarouselProps {
  items: BannerItem[]
}

export default function BannerCarousel({ items }: BannerCarouselProps) {
  const { t } = useTranslation()
  const [activeIndex, setActiveIndex] = useState(0)
  const [isPaused, setIsPaused] = useState(false)

  const handleIndicatorClick = (index: number) => {
    setActiveIndex(index)
  }

  useEffect(() => {
    // WCAG 2.2.2: auto-rotating content must be stoppable. Pausing on hover and
    // on keyboard focus gives both pointer and keyboard users a way out without
    // adding a control that would sit on top of the CMS-authored artwork.
    if (isPaused || items.length < 2) return
    const interval = setInterval(() => {
      setActiveIndex((prevIndex) => (prevIndex + 1) % items.length)
    }, 4000)
    return () => clearInterval(interval)
  }, [items.length, activeIndex, isPaused])

  return (
    <div
      className="w-full min-h-[360px] relative rounded-lg overflow-hidden flex"
      aria-roledescription="carousel"
      aria-label={t('banner.carouselAriaLabel')}
      onMouseEnter={() => setIsPaused(true)}
      onMouseLeave={() => setIsPaused(false)}
      onFocus={() => setIsPaused(true)}
      onBlur={() => setIsPaused(false)}
    >
      <div
        className="w-full h-full flex transition-transform duration-500 ease-in-out"
        style={{ transform: `translateX(-${activeIndex * 100}%)` }}
      >
        {items.map((item, idx) => (
          <div
            key={idx}
            // No role="img" here: it would hide the heading, copy and CTA inside
            // from assistive tech. The slide's name is already its <h2>, and the
            // background is decoration.
            role="group"
            aria-roledescription="slide"
            aria-label={t('banner.goToSlideAriaLabel', { number: idx + 1 })}
            // Off-screen slides stay in the DOM (they're translated, not unmounted),
            // so they must be hidden from AT and from tab order.
            aria-hidden={idx !== activeIndex}
            inert={idx !== activeIndex}
            className="min-w-full h-full bg-cover bg-center bg-no-repeat"
            style={{ backgroundImage: `url(${item.asset?.preview})` }}
          >
            <div className="w-full h-full bg-black/40">
              <BannerContent name={item.name} description={item.description} button={item.button} />
            </div>
          </div>
        ))}
      </div>
      {items.length > 1 && (
        <div className="flex items-center justify-center absolute bottom-[5%] w-full">
          {items.map((_item, index) => {
            return (
              <BannerCarouselIndicator
                key={index}
                isActive={index === activeIndex}
                label={t('banner.goToSlideAriaLabel', { number: index + 1 })}
                onClick={() => handleIndicatorClick(index)}
              />
            )
          })}
        </div>
      )}
    </div>
  )
}
